import { useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import FutureBoardProgress from "./FutureBoardProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import {
  formatDateTime, listFutureCardRevisions, parseFutureBoardError,
  type FutureBoardFailure, type FutureCardText, type FutureRevision,
} from "@chronospark/shared";
import FailureAlert from "./FailureAlert";

type Translate = (key: string, options?: object) => string;

const MAX_REVISIONS = 20;

interface Props {
  open: boolean;
  cardId: number;
  /** いまの版（一覧に「いまの版」と示す） */
  currentVersion: number;
  accessToken?: string;
  language: string;
  t: Translate;
  onClose: () => void;
  /** 「この版に戻す」。編集欄に反映するだけで、保存は通常の保存で行う */
  onRestore: (snapshot: FutureCardText) => void;
}

function Field({ label, value, t }: { label: string; value: string | null | undefined; t: Translate }) {
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      {value ? (
        <Typography sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{value}</Typography>
      ) : (
        <Typography color="text.disabled">{t("futureBoard.scene.notWritten")}</Typography>
      )}
    </Box>
  );
}

// 変更履歴：直近20版の文章を見て、選んだ版を編集欄に戻せる（画像は含まれない）。
function RevisionDialog({ open, cardId, currentVersion, accessToken, language, t, onClose, onRestore }: Props) {
  const [revisions, setRevisions] = useState<FutureRevision[] | null>(null);
  const [selected, setSelected] = useState<FutureRevision | null>(null);
  const [failure, setFailure] = useState<FutureBoardFailure | null>(null);

  useEffect(() => {
    if (!open || !accessToken) return;
    setRevisions(null);
    setSelected(null);
    setFailure(null);
    listFutureCardRevisions(accessToken, cardId)
      .then(result => {
        setRevisions(result.revisions);
        setSelected(result.revisions[0] ?? null);
      })
      .catch(error => setFailure(parseFutureBoardError(error)));
  }, [open, accessToken, cardId]);

  // 履歴の日時は、端末の時刻の設定で表示する（記録の日付のような基準の日付ではないため）。
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return (
    <Dialog open={open} onClose={onClose} aria-labelledby="future-history-title" maxWidth="md" fullWidth scroll="paper">
      <DialogTitle id="future-history-title">{t("futureBoard.history.title")}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("futureBoard.history.intro", { max: MAX_REVISIONS })}
        </Typography>
        {failure && <FailureAlert failure={failure} t={t} />}
        {!revisions && !failure && <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><FutureBoardProgress aria-label={t("Common.loading")} /></Box>}
        {revisions && revisions.length === 0 && <Typography color="text.secondary">{t("futureBoard.history.empty")}</Typography>}
        {revisions && revisions.length > 0 && (
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <List dense sx={{ width: { md: 260 }, flexShrink: 0, maxHeight: { md: 420 }, overflowY: "auto", border: "1px solid", borderColor: "divider", borderRadius: 1 }} aria-label={t("futureBoard.history.title")}>
              {revisions.map(revision => (
                <ListItemButton key={revision.version} selected={selected?.version === revision.version} onClick={() => setSelected(revision)}>
                  <ListItemText
                    primary={t("futureBoard.history.version", { version: revision.version })}
                    secondary={formatDateTime(revision.created_at, language, zone)}
                  />
                  {revision.version === currentVersion && <Chip size="small" label={t("futureBoard.history.current")} />}
                </ListItemButton>
              ))}
            </List>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {selected && (
                <>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>{t("futureBoard.history.preview")}</Typography>
                  <Field label={t("futureBoard.edit.titleLabel")} value={selected.snapshot.title} t={t} />
                  <Field label={t("futureBoard.edit.domainsLabel")} value={selected.snapshot.domains.join("、")} t={t} />
                  <Field label={t("futureBoard.edit.meaningLabel")} value={selected.snapshot.meaning} t={t} />
                  <Field label={t("futureBoard.edit.selfImageLabel")} value={selected.snapshot.self_image} t={t} />
                  <Field label={t("futureBoard.edit.sceneLabel")} value={selected.snapshot.scene} t={t} />
                  <Field label={t("futureBoard.edit.sensationsLabel")} value={selected.snapshot.sensations.join("、")} t={t} />
                  <Alert severity="info" sx={{ mt: 1 }}>{t("futureBoard.history.restoreNote")}</Alert>
                </>
              )}
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("futureBoard.actions.close")}</Button>
        <Button
          variant="contained"
          disabled={!selected || selected.version === currentVersion}
          onClick={() => selected && onRestore(selected.snapshot)}

        >
          {t("futureBoard.history.restore")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default RevisionDialog;
